// /playback/:deviceId — cloud recording playback (spec 17 §2/§3/§5).
//
// Data flow: date → local-day µs window → GET /v3/devices/{id}/recordings
// (items carry clip_id; this build returns DESCENDING clip_start_ts with NO
// cursor — verified live — so deeper pages are fetched by walking `to` down
// past the oldest loaded clip) → clip list + <video> on the signed url.
// URL expiry → GET /v3/recordings/{clip_id}/url refetched ONCE per attempt.
// Event markers come from GET /v3/devices/{id}/events over the same window.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { apiFetch, ApiError } from '../../lib/api';
import { Illustration } from '../../components/Illustration';
import { CaptureControls } from '../capture/CaptureControls';
import { PipCastControls } from '../pip/PipCastControls';
import { formatDuration, formatTimestampUs } from '../../lib/format';
import { dayWindowUs, dateFromUsSafe, fetchAllEventsInWindow, loadFirstRecordings, loadMoreRecordings, toDateInputValue, type DeviceSummary, type EventItem } from './playbackLib';
import { deviceName } from '../devices/deviceModel';
import { TimelineStrip } from './TimelineStrip';
import './playback.css';

interface RecordingClip {
  clip_id: string;
  clip_start_ts: number;
  clip_end_ts: number;
  has_event: boolean;
  quality?: string;
  url: string;
}

type Gate = 'sdcard' | 'forbidden' | null;
type LoadState = 'loading' | 'ready' | 'error';

const GAP_US = 2_000_000; // >2s between clips = no-recording gap (spec 17 §3.1)
const PLAYBACK_SPEEDS = [0.5, 1, 1.5, 2, 4] as const; // 倍速選項（APP#8，對齊 Vistaflow M5）

const timeOnly = (us: number) => formatTimestampUs(us, { year: undefined, month: undefined, day: undefined });

function urlStillValid(url: string): boolean {
  try {
    const exp = new URL(url).searchParams.get('exp');
    if (!exp) return true;
    return Number(exp) * 1000 > Date.now() + 5000;
  } catch {
    return true;
  }
}

export default function PlaybackPage() {
  const { t } = useTranslation();
  const { deviceId } = useParams<{ deviceId: string }>();
  const [searchParams] = useSearchParams();
  const seekTsUs = useMemo(() => {
    const t = Number(searchParams.get('t'));
    return Number.isFinite(t) && t > 0 ? t : null;
  }, [searchParams]);

  const [date, setDate] = useState(() => toDateInputValue(seekTsUs ? dateFromUsSafe(seekTsUs) : new Date()));
  const [device, setDevice] = useState<DeviceSummary | null>(null);
  const [clips, setClips] = useState<RecordingClip[]>([]);
  // spec 17 §2.2 lazy list model: `reachedStart` = the day's oldest range is loaded (no more
  // pages); `loadingMore` guards the scroll-bottom / scrubber-boundary "load older" trigger.
  const [reachedStart, setReachedStart] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [loadState, setLoadState] = useState<LoadState>('loading');
  const [loadError, setLoadError] = useState<string | null>(null);
  const [gate, setGate] = useState<Gate>(null);
  const [eventsInDay, setEventsInDay] = useState<EventItem[]>([]);
  const [reloadKey, setReloadKey] = useState(0);
  const [toast, setToast] = useState<string | null>(null);

  // player state
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const [currentIdx, setCurrentIdx] = useState<number | null>(null);
  const [src, setSrc] = useState<string | null>(null);
  const [rate, setRate] = useState(1); // 倍速（APP#8）
  const [downloading, setDownloading] = useState(false);
  // Global playhead in µs (current clip start + video offset) for the timeline strip.
  const [playheadUs, setPlayheadUs] = useState<number | null>(null);
  const pendingSeekSecRef = useRef<number | null>(null);
  const refreshedThisAttemptRef = useRef(false);
  const urlOverrides = useRef(new Map<string, string>());
  const deepLinkAppliedRef = useRef(false);
  const toastTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const isOwner = device?.role === 'owner';
  const win = useMemo(() => dayWindowUs(date), [date]);

  const showToast = useCallback((msg: string) => {
    setToast(msg);
    if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
    toastTimerRef.current = setTimeout(() => setToast(null), 4000);
  }, []);
  useEffect(() => () => {
    if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
  }, []);

  // 空日不再是死路(design review P0-2):查最近一筆雲端錄影 → 跳到該日。
  // recordings 為 clip_start_ts 降冪(本 build,§2.1),wide 視窗 limit=1 即最新。
  const [jumping, setJumping] = useState(false);
  const jumpToLatest = useCallback(async () => {
    if (!deviceId) return;
    setJumping(true);
    try {
      const nowUs = Date.now() * 1000;
      const res = await apiFetch<RecordingClip[]>(`/v3/devices/${encodeURIComponent(deviceId)}/recordings`, {
        query: { from: nowUs - 90 * 86_400_000_000, to: nowUs, limit: 1 },
      });
      const latest = (res.data ?? [])[0];
      if (latest) setDate(toDateInputValue(dateFromUsSafe(latest.clip_start_ts)));
      else showToast(t('playback.toastNoRecordingsInRetention'));
    } catch {
      showToast(t('playback.toastJumpLatestFailed'));
    } finally {
      setJumping(false);
    }
  }, [deviceId, showToast, t]);

  // ---- device info (role for upsell mask §5.2, recording.location for sdcard §5.3) ----
  useEffect(() => {
    if (!deviceId) return;
    let alive = true;
    apiFetch<DeviceSummary[]>('/v3/devices')
      .then((res) => {
        if (!alive) return;
        const d = (res.data ?? []).find((x) => x.device_id === deviceId) ?? null;
        setDevice(d);
        if (d?.recording?.location === 'sdcard') setGate('sdcard');
      })
      .catch(() => {
        /* best-effort; clips fetch is the authority for hard failures */
      });
    return () => {
      alive = false;
    };
  }, [deviceId, reloadKey]);

  // ---- clip list for the selected local day (spec 17 §2.2 lazy, cache-first) ----
  // Load ONLY the first (newest) range on entry — no eager whole-day drain. Older ranges are
  // pulled on demand by loadMore() (scroll bottom / scrubber boundary). loadFirstRecordings is
  // cache-first: re-opening a day this session repaints from memory with no network.
  useEffect(() => {
    if (!deviceId || gate !== null) return;
    let alive = true;
    setLoadState('loading');
    setLoadError(null);
    setClips([]);
    setReachedStart(false);
    setLoadingMore(false);
    setCurrentIdx(null);
    setSrc(null);
    urlOverrides.current.clear();

    loadFirstRecordings(deviceId, win)
      .then((res) => {
        if (!alive) return;
        setClips(res.clips);
        setReachedStart(res.reachedStart);
        setLoadState('ready');
      })
      .catch((e: unknown) => {
        if (!alive) return;
        if (e instanceof ApiError && e.status === 403) {
          // ADR-024: 撤權 / permission changed — 下次 fetch 生效 (spec 17 §3.3 point 2)
          setGate('forbidden');
        } else {
          // 回放不受訂閱閘 (spec 17 §3.3 point 1;後端 media.go 只驗裝置關係)。
          // 422 不再視為「訂閱失效」——落一般錯誤,不擋播/不踢訂閱頁。
          setLoadState('error');
          setLoadError(e instanceof ApiError ? `${e.message}（${e.code}）` : t('common.networkErrorRetry'));
        }
      });
    return () => {
      alive = false;
    };
  }, [deviceId, win.fromUs, win.toUs, gate, reloadKey, t]);

  // Pull the next older range (spec 17 §2.2). Idempotent while a page is in flight or the day
  // start is reached; the playbackLib pager dedupes concurrent calls and serves from cache.
  const loadingMoreRef = useRef(false);
  const loadMore = useCallback(async (): Promise<RecordingClip[] | null> => {
    if (!deviceId || gate !== null || reachedStart || loadingMoreRef.current) return null;
    loadingMoreRef.current = true;
    setLoadingMore(true);
    try {
      const res = await loadMoreRecordings(deviceId, win);
      setClips(res.clips);
      setReachedStart(res.reachedStart);
      return res.clips;
    } catch {
      // transient — the trigger fires again on the next scroll/scrub
      return null;
    } finally {
      loadingMoreRef.current = false;
      setLoadingMore(false);
    }
  }, [deviceId, gate, reachedStart, win.fromUs, win.toUs]);

  // Scroll-bottom trigger (spec 17 §2.2): observe a sentinel at the end of the (newest→oldest)
  // clip list; when it scrolls into view and older ranges remain, pull the next earlier range.
  const bottomSentinelRef = useRef<HTMLLIElement | null>(null);
  useEffect(() => {
    const el = bottomSentinelRef.current;
    if (!el || loadState !== 'ready' || reachedStart) return;
    if (typeof IntersectionObserver === 'undefined') return; // jsdom / SSR: no scroll trigger
    const io = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) void loadMore();
      },
      { rootMargin: '200px' },
    );
    io.observe(el);
    return () => io.disconnect();
  }, [loadState, reachedStart, loadMore, clips.length]);

  // ---- event markers for the same window (best-effort; spec 17 §4.3) ----
  useEffect(() => {
    if (!deviceId || gate) return;
    let alive = true;
    setEventsInDay([]);
    fetchAllEventsInWindow(deviceId, win.fromUs, win.toUs)
      .then((evs) => {
        if (alive) setEventsInDay(evs);
      })
      .catch(() => {
        /* markers are decorative — list still works without them */
      });
    return () => {
      alive = false;
    };
  }, [deviceId, win.fromUs, win.toUs, gate, reloadKey]);

  const eventCountByClip = useMemo(() => {
    const m = new Map<string, number>();
    if (clips.length === 0 || eventsInDay.length === 0) return m;
    for (const ev of eventsInDay) {
      const clip = clips.find((c) => c.clip_start_ts <= ev.event_ts && ev.event_ts <= c.clip_end_ts);
      if (clip) m.set(clip.clip_id, (m.get(clip.clip_id) ?? 0) + 1);
    }
    return m;
  }, [clips, eventsInDay]);

  // ---- url refresh (GET /v3/recordings/{clip_id}/url, spec 17 §3.3) ----
  const refreshClipUrl = useCallback(
    async (clip: RecordingClip): Promise<string | null> => {
      // orphan index rows can carry clip_id:"" — refreshing would hit
      // GET /v3/recordings//url (301→404); just fail the attempt locally
      if (!clip.clip_id) return null;
      try {
        const res = await apiFetch<{ url: string }>(`/v3/recordings/${encodeURIComponent(clip.clip_id)}/url`);
        const url = res.data?.url;
        if (!url) return null;
        urlOverrides.current.set(clip.clip_id, url);
        return url;
      } catch (e) {
        if (e instanceof ApiError && e.status === 403) setGate('forbidden');
        // 422 不視為訂閱失效(回放不受訂閱閘,spec 17 §3.3);換發失敗即回 null。
        return null;
      }
    },
    []
  );

  const playClip = useCallback(
    // `list` defaults to the current `clips` state, but the scrubber-boundary path passes the
    // freshly-grown list it resolved `idx` against: after loadMore() the `.then` runs as a
    // microtask BEFORE React re-renders, so this closure's `clips` is still the pre-load array
    // (older clips get prepended → every index shifts). Looking `clip` up in `list[idx]` keeps
    // idx and clip consistent; `idx` is the eventual index into the committed `clips` state too.
    async (idx: number, seekUs?: number, list: RecordingClip[] = clips) => {
      const clip = list[idx];
      if (!clip) return;
      setCurrentIdx(idx);
      refreshedThisAttemptRef.current = false;
      pendingSeekSecRef.current = seekUs !== undefined ? Math.max(0, (seekUs - clip.clip_start_ts) / 1_000_000) : null;
      let url = urlOverrides.current.get(clip.clip_id) ?? clip.url;
      if (!urlStillValid(url)) {
        // pre-emptive refresh: signed url already past `exp`
        refreshedThisAttemptRef.current = true;
        const fresh = await refreshClipUrl(clip);
        if (!fresh) {
          showToast(t('playback.toastUrlFetchFailed'));
          return;
        }
        url = fresh;
      }
      setSrc(url);
    },
    [clips, refreshClipUrl, showToast, t]
  );

  // ---- download / save this clip (spec 17 §3.4) ----
  // Re-fetch a FRESH presigned url for the clip (the cached one may be past exp)
  // and let the browser download the fMP4. No backend beyond the existing
  // GET /v3/recordings/{clip_id}/url — the signed url points straight at the bytes
  // (S3 presigned GET in prod; clipdl signed url in dev). The `download` attribute
  // is best-effort: cross-origin presigned urls may still open a tab, but the
  // Content-Disposition on the object drives the save either way.
  const downloadClip = useCallback(
    async (clip: RecordingClip) => {
      if (!clip.clip_id || downloading) return;
      setDownloading(true);
      try {
        // always re-sign: a stale override could already be expired
        const fresh = await refreshClipUrl(clip);
        const url = fresh ?? urlOverrides.current.get(clip.clip_id) ?? clip.url;
        if (!url) {
          showToast(t('playback.toastDownloadUrlFailed'));
          return;
        }
        const startDate = dateFromUsSafe(clip.clip_start_ts);
        const pad = (n: number) => String(n).padStart(2, '0');
        const name = `${deviceId}_${startDate.getFullYear()}${pad(startDate.getMonth() + 1)}${pad(startDate.getDate())}_${pad(startDate.getHours())}${pad(startDate.getMinutes())}${pad(startDate.getSeconds())}.mp4`;
        const a = document.createElement('a');
        a.href = url;
        a.download = name;
        a.rel = 'noopener';
        document.body.appendChild(a);
        a.click();
        a.remove();
        showToast(t('playback.toastDownloadStarted'));
      } finally {
        setDownloading(false);
      }
    },
    [deviceId, downloading, refreshClipUrl, showToast, t]
  );

  // ---- deep-link seek from ?t= (event → clip, spec 17 §4.3/§4.4) ----
  useEffect(() => {
    deepLinkAppliedRef.current = false;
  }, [seekTsUs, deviceId]);
  useEffect(() => {
    if (loadState !== 'ready' || seekTsUs === null || deepLinkAppliedRef.current || clips.length === 0) return;
    deepLinkAppliedRef.current = true;
    const hitIdx = clips.findIndex((c) => c.clip_start_ts <= seekTsUs && seekTsUs <= c.clip_end_ts);
    if (hitIdx >= 0) {
      void playClip(hitIdx, seekTsUs);
      return;
    }
    const nextIdx = clips.findIndex((c) => c.clip_start_ts >= seekTsUs);
    if (nextIdx >= 0) {
      showToast(t('playback.toastSeekToNearest'));
      void playClip(nextIdx);
    } else {
      showToast(t('playback.toastNoRecordingAfter'));
    }
  }, [loadState, seekTsUs, clips, playClip, showToast, t]);

  // ---- video element handlers ----
  const onLoadedMetadata = useCallback(() => {
    const v = videoRef.current;
    if (!v) return;
    // 換片段時瀏覽器把 playbackRate 重置回 1 → 在 metadata 就緒後重套選定倍速（APP#8）
    v.playbackRate = rate;
    const seek = pendingSeekSecRef.current;
    if (seek !== null && seek > 0) v.currentTime = seek;
    pendingSeekSecRef.current = null;
    v.play().catch(() => {
      /* autoplay policy — user can press play */
    });
  }, [rate]);

  // 倍速即時生效（播放中切速）；換 src 由 onLoadedMetadata 重套（APP#8）
  useEffect(() => {
    if (videoRef.current) videoRef.current.playbackRate = rate;
  }, [rate]);

  const onVideoError = useCallback(() => {
    if (currentIdx === null) return;
    const clip = clips[currentIdx];
    if (!clip) return;
    if (!refreshedThisAttemptRef.current) {
      // most likely S3 AccessDenied on an expired url → refetch ONCE and resume in place
      refreshedThisAttemptRef.current = true;
      const v = videoRef.current;
      if (v && v.currentTime > 0) pendingSeekSecRef.current = v.currentTime;
      void refreshClipUrl(clip).then((url) => {
        if (url) setSrc(url);
        else showToast(t('playback.toastClipUnplayable'));
      });
    } else {
      showToast(t('playback.toastClipUnplayable'));
    }
  }, [currentIdx, clips, refreshClipUrl, showToast, t]);

  const onEnded = useCallback(() => {
    if (currentIdx === null) return;
    const cur = clips[currentIdx];
    const next = clips[currentIdx + 1];
    if (!next || !cur) {
      showToast(t('playback.toastReachedLast'));
      return;
    }
    if (next.clip_start_ts - cur.clip_end_ts > GAP_US) showToast(t('playback.toastGapSkippedNext'));
    void playClip(currentIdx + 1);
  }, [currentIdx, clips, playClip, showToast, t]);

  // ---- timeline playhead: video offset → global µs (spec 17 §3.1) ----
  const onTimeUpdate = useCallback(() => {
    const v = videoRef.current;
    if (!v || currentIdx === null) return;
    const clip = clips[currentIdx];
    if (clip) setPlayheadUs(clip.clip_start_ts + Math.round(v.currentTime * 1_000_000));
  }, [currentIdx, clips]);

  // ---- timeline seek: µs → clip (containing, else nearest forward, spec 17 §4.3) ----
  // Resolve a seek against a given clip list (containing clip → play at offset; else the
  // nearest forward clip). Kept list-parametric so a scrubber-boundary loadMore can retry
  // against the freshly grown list without waiting for a React re-render.
  const resolveSeek = useCallback(
    (list: RecordingClip[], us: number) => {
      const hit = list.findIndex((c) => c.clip_start_ts <= us && us <= c.clip_end_ts);
      if (hit >= 0) {
        void playClip(hit, us, list);
        return;
      }
      const next = list.findIndex((c) => c.clip_start_ts >= us);
      if (next >= 0) {
        showToast(t('playback.toastSeekToNearest'));
        void playClip(next, undefined, list);
      } else {
        showToast(t('playback.toastNoRecordingAfter'));
      }
    },
    [playClip, showToast, t]
  );
  const seekToUs = useCallback(
    (us: number) => {
      if (clips.length === 0) return;
      setPlayheadUs(us);
      // Scrubber pulled before the oldest-loaded clip and older ranges remain → pull the next
      // earlier range (spec 17 §2.2 scrubber-boundary trigger), then resolve against the grown list.
      if (!reachedStart && us < clips[0].clip_start_ts) {
        showToast(t('playback.loadingClips'));
        // A5 review #3: a deep jump may be older than a single page — keep paging until the
        // target is covered (or start reached / no progress / cap), not one page per gesture.
        void (async () => {
          let list = clips;
          for (let i = 0; i < 20; i++) {
            if (list.length && us >= list[0].clip_start_ts) break;
            const prevOldest = list.length ? list[0].clip_start_ts : Number.POSITIVE_INFINITY;
            const grown = await loadMore();
            if (!grown) break; // reachedStart / gate / inflight
            list = grown;
            if (list.length && list[0].clip_start_ts >= prevOldest) break; // no progress → stop
          }
          resolveSeek(list, us);
        })();
        return;
      }
      resolveSeek(clips, us);
    },
    [clips, reachedStart, loadMore, resolveSeek, showToast, t]
  );

  // ---- guards / gates ----

  if (!deviceId) {
    return (
      <section className="card" style={{ maxWidth: 560 }}>
        <p className="error-text" style={{ margin: 0 }}>
          {t('playback.missingDeviceIdPrefix')} <Link to="/devices">{t('playback.devicesLink')}</Link> {t('playback.missingDeviceIdSuffix')}
        </p>
      </section>
    );
  }

  if (gate === 'sdcard') {
    return (
      <section className="card pb-gate">
        <h1 className="pb-gate-title">{t('playback.gateSdcardTitle')}</h1>
        <p className="text-secondary">{t('playback.gateSdcardBody')}</p>
        {/* SD 卡本地回放 v1 明確不做（GAP-PB-3）；owner 可切回雲端（ADR-046 經裝置設定頁 op:set，入口在裝置設定頁） */}
        {isOwner && (
          <Link className="btn-primary pb-gate-cta" to={`/devices/${encodeURIComponent(deviceId)}`}>
            {t('playback.gateSdcardCta')}
          </Link>
        )}
      </section>
    );
  }

  if (gate === 'forbidden') {
    return (
      <section className="card pb-gate" role="alert">
        <h1 className="pb-gate-title">{t('playback.gateForbiddenTitle')}</h1>
        <p className="text-secondary">{t('playback.gateForbiddenBody')}</p>
        <Link className="btn-ghost pb-gate-cta" to="/devices">
          {t('playback.gateForbiddenCta')}
        </Link>
      </section>
    );
  }

  // 〔已移除〕舊 gate === 'subscription' 分支:回放不受訂閱閘(spec 17 §3.3),
  // 該 gate 已無觸發點(has_event 422 誤判移除後成死碼)。

  // ---- main view ----

  const currentClip = currentIdx !== null ? clips[currentIdx] : undefined;

  // rows with gap dividers (spec 17 §3.1 縫隙). Rendered NEWEST→OLDEST (spec 17 §2.2): the
  // newest range sits at the top, and the bottom = the oldest-loaded clip, so scrolling to
  // the bottom sentinel pulls the next earlier range. `idx` stays the ascending-array index
  // so player/seek math (playClip, onEnded currentIdx+1) is unchanged.
  const rows: ({ kind: 'clip'; idx: number } | { kind: 'gap'; sec: number; key: string })[] = [];
  for (let i = clips.length - 1; i >= 0; i--) {
    rows.push({ kind: 'clip', idx: i });
    if (i > 0) {
      const older = clips[i - 1];
      const gapUs = clips[i].clip_start_ts - older.clip_end_ts;
      if (gapUs > GAP_US) rows.push({ kind: 'gap', sec: gapUs / 1_000_000, key: `gap-${older.clip_id}` });
    }
  }

  return (
    <section className="pb-page">
      <header className="pb-header">
        <h1 style={{ margin: 0 }}>{t('playback.headerTitle', { name: device ? deviceName(device) : deviceId })}</h1>
        <Link to={`/events/${encodeURIComponent(deviceId)}`}>{t('playback.eventTimelineLink')}</Link>
        {/* 多機同步回放（APP#43）：帶本機預選進共用時鐘多機頁 */}
        <Link to={`/playback/multi?ids=${encodeURIComponent(deviceId)}`}>{t('playback.multiCamLink')}</Link>
      </header>

      <div className="pb-toolbar">
        <input type="date" value={date} onChange={(e) => setDate(e.target.value)} aria-label={t('playback.datePickerLabel')} className="pb-date" />
        {/* 「只看有事件」開關已移除:雲端 clip 皆事件錨點,篩選 no-op 且會送 PARKED 的 has_event → 422（spec 17 §2.1／AC-PB-2） */}
        {/* 分享連結 blocked on GAP-PB-7（presigned URL 不得外傳充當分享連結，spec 17 §3.5） */}
        <button className="btn-ghost pb-share" disabled title={t('playback.shareLinkComingTitle')}>
          {t('playback.shareLinkComing')}
        </button>
      </div>

      {toast && (
        <p className="pb-toast" role="status">
          {toast}
        </p>
      )}

      <div className="pb-player card">
        {src ? (
          <video
            ref={videoRef}
            className="pb-video"
            src={src}
            controls
            playsInline
            onLoadedMetadata={onLoadedMetadata}
            onError={onVideoError}
            onEnded={onEnded}
            onTimeUpdate={onTimeUpdate}
          />
        ) : (
          <div className="pb-video pb-video-empty text-secondary">{clips.length > 0 ? t('playback.selectClipToPlay') : t('playback.noPlayableClip')}</div>
        )}
        {currentClip && (
          <div className="pb-nowplaying-row">
            <p className="text-secondary pb-nowplaying">
              {t('playback.nowPlaying', { start: timeOnly(currentClip.clip_start_ts), end: timeOnly(currentClip.clip_end_ts) })}
              {currentClip.quality ? ` · ${currentClip.quality}` : ''}
            </p>
            {/* 倍速播放（APP#8，對齊 Vistaflow M5）：套用 video.playbackRate，換片段後保持 */}
            <label className="pb-speed-wrap">
              <span className="text-secondary">{t('playback.speed')}</span>
              <select
                className="pb-speed"
                data-testid="pb-speed"
                aria-label={t('playback.speedLabel')}
                value={rate}
                onChange={(e) => setRate(Number(e.target.value))}
              >
                {PLAYBACK_SPEEDS.map((s) => (
                  <option key={s} value={s}>
                    {s}×
                  </option>
                ))}
              </select>
            </label>
            {/* 下載這段（spec 17 §3.4）：重簽短效 URL → 瀏覽器下載 fMP4，無需後端 */}
            <button className="btn-ghost pb-download" disabled={downloading} onClick={() => void downloadClip(currentClip)}>
              {downloading ? t('playback.preparingDownload') : t('playback.downloadClip')}
            </button>
            {/* 本機截圖 / 錄影（APP#17）：截目前畫面 / 用 captureStream() 錄當前播放 */}
            <CaptureControls
              videoRef={videoRef}
              mediaStream={null}
              deviceName={device ? deviceName(device) : deviceId}
              deviceId={deviceId}
              ready={!!src}
            />
            {/* 懸浮窗 / 投屏（APP#18）：PiP 邊滑邊看 + Remote Playback 投到大螢幕 */}
            <PipCastControls videoRef={videoRef} ready={!!src} />
          </div>
        )}
      </div>

      {/* 24h 時間軸 scrubber（REF lsappv3 sd_timeline + hls_timebar）：段色塊 + 事件標記 + 拖曳 seek */}
      {loadState === 'ready' && clips.length > 0 && (
        <TimelineStrip
          range={win}
          clips={clips}
          events={eventsInDay}
          playheadUs={playheadUs}
          onSeek={seekToUs}
        />
      )}

      {loadState === 'loading' && <p className="text-secondary">{t('playback.loadingClips')}</p>}

      {loadState === 'error' && (
        <div className="card" role="alert" style={{ maxWidth: 560 }}>
          <p className="error-text" style={{ marginTop: 0 }}>
            {t('playback.loadClipsFailed', { error: loadError })}
          </p>
          <button className="btn-ghost" onClick={() => setReloadKey((k) => k + 1)}>
            {t('common.retry')}
          </button>
        </div>
      )}

      {loadState === 'ready' && clips.length === 0 && (
        <div className="card" style={{ maxWidth: 560, textAlign: 'center' }}>
          <Illustration name="no-recordings" alt="" size={160} />
          <p style={{ marginTop: 0 }}>{t('empty.noRecordings')}</p>
          <p className="text-secondary" style={{ marginBottom: 'var(--spacing-4)' }}>
            {t('playback.emptyTryAnotherDate')}
          </p>
          <button className="btn-ghost" data-testid="pb-jump-latest" disabled={jumping} onClick={() => void jumpToLatest()}>
            {jumping ? t('playback.searching') : t('playback.jumpToLatest')}
          </button>
        </div>
      )}

      {loadState === 'ready' && clips.length > 0 && (
        <ul className="pb-cliplist" aria-label={t('playback.clipListLabel')}>
          {rows.map((row) =>
            row.kind === 'gap' ? (
              <li key={row.key} className="pb-gap text-secondary" aria-hidden>
                {t('playback.gapNoRecording', { duration: formatDuration(row.sec) })}
              </li>
            ) : (
              (() => {
                const clip = clips[row.idx];
                if (!clip) return null;
                const evCount = eventCountByClip.get(clip.clip_id) ?? 0;
                const active = currentIdx === row.idx;
                return (
                  <li key={clip.clip_id}>
                    <button className={`pb-clip ${active ? 'active' : ''}`} onClick={() => void playClip(row.idx)}>
                      <span className="pb-clip-time">
                        {timeOnly(clip.clip_start_ts)} – {timeOnly(clip.clip_end_ts)}
                      </span>
                      <span className="pb-clip-meta text-secondary">
                        {formatDuration((clip.clip_end_ts - clip.clip_start_ts) / 1_000_000)}
                        {clip.quality ? ` · ${clip.quality}` : ''}
                      </span>
                      <span className="pb-clip-badges">
                        {clip.has_event && <span className="pb-badge pb-badge-event">{t('playback.eventBadge')}{evCount > 0 ? ` ×${evCount}` : ''}</span>}
                        {active && <span className="pb-badge pb-badge-playing">{t('playback.playingBadge')}</span>}
                      </span>
                    </button>
                  </li>
                );
              })()
            )
          )}
          {/* Load-older sentinel (spec 17 §2.2): the IntersectionObserver above watches this
              row; while older ranges remain it shows a spinner, else a quiet end-of-list note. */}
          {!reachedStart ? (
            <li ref={bottomSentinelRef} className="pb-cliplist-more text-secondary" aria-hidden={!loadingMore}>
              {loadingMore ? t('playback.loadingClips') : ''}
            </li>
          ) : (
            clips.length >= 100 && (
              <li className="pb-cliplist-end text-secondary" aria-hidden>
                {t('playback.reachedListStart')}
              </li>
            )
          )}
        </ul>
      )}
    </section>
  );
}
